/** Typed Clari API endpoints, grouped by domain. */
import { apiRequest } from '@/lib/api'
import type {
  AuthUser,
  KycStartResponse,
  KycStatusResponse,
  LoginResponse,
  SessionResponse,
} from '@/lib/types'

export interface RegisterPayload {
  email: string
  password: string
  preferred_language: string
  country_residence: string
  first_name?: string
  last_name?: string
  plan?: string
  accept_terms: true
}

export const authApi = {
  register: (payload: RegisterPayload) =>
    apiRequest<SessionResponse>('/auth/register', { method: 'POST', body: payload, auth: false }),

  login: (email: string, password: string, twoFactorCode?: string) =>
    apiRequest<LoginResponse>('/auth/login', {
      method: 'POST',
      auth: false,
      body: { email, password, ...(twoFactorCode ? { two_factor_code: twoFactorCode } : {}) },
    }),

  logout: (refreshToken?: string) =>
    apiRequest<void>('/auth/logout', {
      method: 'POST',
      body: refreshToken ? { refresh_token: refreshToken } : {},
    }),

  me: () => apiRequest<{ user: AuthUser }>('/auth/me'),

  verifyEmail: (token: string) =>
    apiRequest<{ user: AuthUser }>('/auth/verify-email', {
      method: 'POST',
      body: { token },
      auth: false,
    }),

  resendVerification: () => apiRequest<void>('/auth/resend-verify-email', { method: 'POST' }),

  forgotPassword: (email: string) =>
    apiRequest<void>('/auth/forgot-password', { method: 'POST', body: { email }, auth: false }),

  resetPassword: (token: string, password: string) =>
    apiRequest<void>('/auth/reset-password', {
      method: 'POST',
      body: { token, password },
      auth: false,
    }),

  changePassword: (currentPassword: string, newPassword: string) =>
    apiRequest<void>('/auth/change-password', {
      method: 'POST',
      body: { current_password: currentPassword, new_password: newPassword },
    }),

  twoFactor: {
    enroll: () =>
      apiRequest<{ secret: string; otpauth_url: string }>('/auth/2fa/enroll', { method: 'POST' }),
    verify: (token: string) =>
      apiRequest<{ backup_codes: string[] }>('/auth/2fa/verify', { method: 'POST', body: { token } }),
    disable: (password: string) =>
      apiRequest<void>('/auth/2fa/disable', { method: 'POST', body: { password } }),
    regenerateBackup: () =>
      apiRequest<{ backup_codes: string[] }>('/auth/2fa/backup-codes/regenerate', {
        method: 'POST',
      }),
  },
}

export const kycApi = {
  start: () => apiRequest<KycStartResponse>('/kyc/start', { method: 'POST' }),
  status: () => apiRequest<KycStatusResponse>('/kyc/status'),
  upgradeToFull: () => apiRequest<KycStartResponse>('/kyc/upgrade-to-full', { method: 'POST' }),
}
